import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { SelectField } from 'components/form';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { insuranceActions, insuranceSelector } from '../../../model';
import { CoverageStatusLabels, InsuranceSelectSubmissionForm } from '../../../types';
import { SubmissionDetails } from '../../SubmissionDetails';
import { InsuranceRequirements } from './InsuranceRequirements';
import { InsuranceCoverageRequirements } from './InsuranceCoverageRequirements';
import { checkIfQuillValueIsEmpty } from 'utils/quillUtils';
import { useMutation } from '@apollo/client';
import { UPDATE_FINANCE_PROGRAM } from '../../../api';
import { useNotifications } from 'modules/notification';
import { useStepperState } from 'modules/vendorOpportunity/insurance/hooks/useStepperState';
import { coverageStatusConfig } from 'modules/vendorOpportunity/insurance/constants';

export const InsuranceSelectSubmissionDialog = () => {
  const dispatch = useDispatch();

  const submissions = useSelector(insuranceSelector.submissions);
  const financePrograms = useSelector(insuranceSelector.financePrograms);
  const insuranceBroker = useSelector(insuranceSelector.insuranceBroker);
  const insuranceCertificate = useSelector(insuranceSelector.insuranceCertificate);

  const account = useSelector((state: any) => state.account);
  const lenderProfiles = useSelector((state: any) => state.vp?.lenderProfiles ?? []) as any[];

  const {
    stepperCompleteDetermineLenderRequirements,
    stepperCompleteAgentInformationNeeded,
    stepperCompleteVerifyCoverage,
  } = useStepperState();

  const { isOpen, handleClose } = useModal(ModalsKeys.InsuranceSelectSubmissionDialog);
  const { handleOpen: openConfirmationModal } = useModal(ModalsKeys.Confirmation);

  const { showNotification } = useNotifications();

  const { control, getValues, setValue, handleSubmit } = useFormContext<InsuranceSelectSubmissionForm>();

  const [updateFinanceProgram, { loading }] = useMutation(UPDATE_FINANCE_PROGRAM, {
    onCompleted: () => {
      dispatch(insuranceActions.setSelectedSubmission(formSelectedSubmission));
      dispatch(
        insuranceActions.setSelectedFinanceProgram({
          ...formSelectedFinanceProgram,
          insuranceCoverage: {
            coverageRequirements: getValues('coverageRequirements'),
            certificateRequirements: getValues('certificateRequirements'),
            otherInstructions: getValues('otherInstructions'),
          },
        })
      );

      stepperCompleteDetermineLenderRequirements();
      if (insuranceBroker) {
        stepperCompleteAgentInformationNeeded();
        dispatch(
          insuranceActions.setCoverageStatusConfig(coverageStatusConfig[CoverageStatusLabels.InsuredUnverified])
        );
      }
      if (insuranceCertificate) {
        stepperCompleteVerifyCoverage();
        dispatch(insuranceActions.setCoverageStatusConfig(coverageStatusConfig[CoverageStatusLabels.InsuredVerified]));
      }
      showNotification('Insurance Requirements have been saved');
      handleClose();
    },
  });

  const { submissionId, financeProgramId, coverageRequirements } = useWatch<InsuranceSelectSubmissionForm>({
    control: control,
  });

  const submissionsOptions = useMemo(
    () =>
      submissions?.map(submission => ({
        value: submission.creditSubmissionId,
        label: `${submission.lenderName} - ${submission.submissionDate}`,
      })),
    [submissions]
  );

  const { formSelectedSubmission, financeProgramsOptions } = useMemo(() => {
    const formSelectedSubmission = submissions.find(submission => submission.creditSubmissionId === submissionId);

    const submissionLenderProfile = lenderProfiles.find(
      lp => lp.lenderProfileId === formSelectedSubmission?.lenderProfileId
    );
    const financeProgramsForSelect = financePrograms.filter(fp => {
      const isProgramSharedToCreditSubmissionLender = fp.financeProgramToLenderProfiles?.some(
        i => i?.lenderProfileId === submissionLenderProfile?.id
      );
      const isLenderOwnProgram = submissionLenderProfile?.lenderAccount?.id === fp.accountId;
      const isDealerOwnProgram = account.id === fp.accountId;
      return isProgramSharedToCreditSubmissionLender || isLenderOwnProgram || isDealerOwnProgram;
    });
    const financeProgramsOptions = financeProgramsForSelect?.map(fp => ({
      value: fp.financeProgramId,
      label: fp.nameInternal,
    }));

    return {
      financeProgramsOptions,
      formSelectedSubmission,
    };
  }, [submissionId]);

  const formSelectedFinanceProgram = useMemo(
    () => financePrograms.find(fp => fp.financeProgramId === financeProgramId),
    [financeProgramId]
  );

  const handleSave = (formData: InsuranceSelectSubmissionForm) => {
    openConfirmationModal({
      title: 'Save Insurance Requirements?',
      message: `These requirements and instructions will be applied to the selected submission, and for future use with this Lender Finance Program.`,
      onConfirm: () => saveFinanceProgram(formData),
    });
  };

  const saveFinanceProgram = async (formData: InsuranceSelectSubmissionForm) => {
    try {
      const financeProgram = {
        financeProgramId: formSelectedFinanceProgram?.financeProgramId,
        insuranceCoverage: {
          coverageRequirements: getValues('coverageRequirements'),
          certificateRequirements: getValues('certificateRequirements'),
          otherInstructions: getValues('otherInstructions'),
        },
      };
      await updateFinanceProgram({
        variables: {
          input: financeProgram,
        },
      });
    } catch (error) {
      showNotification('Error', { type: 'error' });
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Typography component="span" variant="h6">
          Select Submission
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack gap="1rem">
          <SelectField
            label="Select Submission"
            control={control}
            name="submissionId"
            options={submissionsOptions || []}
          />
          <SelectField
            label="Finance Program"
            control={control}
            name="financeProgramId"
            options={financeProgramsOptions || []}
            inputProps={{ disabled: !formSelectedSubmission }}
            extraAction={selectedId => {
              const financeProgram = financePrograms.find(fp => fp.financeProgramId === selectedId);
              setValue('coverageRequirements', financeProgram?.insuranceCoverage?.coverageRequirements || '');
              setValue('certificateRequirements', financeProgram?.insuranceCoverage?.certificateRequirements || '');
              setValue('otherInstructions', financeProgram?.insuranceCoverage?.otherInstructions || '');
            }}
          />

          {formSelectedSubmission && formSelectedFinanceProgram && (
            <>
              <SubmissionDetails selectedSubmission={formSelectedSubmission} />
              <InsuranceRequirements selectedFinanceProgram={formSelectedFinanceProgram} />
              <InsuranceCoverageRequirements />
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          loading={loading}
          onClick={handleSubmit(handleSave)}
          variant="contained"
          disabled={checkIfQuillValueIsEmpty(coverageRequirements)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
